import type { FC } from 'react'

import Link from 'next/link'
import { useRef } from 'react'
import { useRouter } from 'next/router'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toFaDigits } from '@/lib/format'
import { animationDelay } from '@/lib/motion'
import { formatRange, getHoursFor } from '@/lib/hours'
import { usePresence } from '@/hooks/use-presence'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import { useBakeryClock } from '@/hooks/use-bakery-clock'
import { useLockBodyScroll } from '@/hooks/use-lock-body-scroll'
import { ButtonLink } from '@/components/generals/button'
import { Wordmark } from '@/components/generals/wordmark'
import { OpenStatus } from '@/components/generals/open-status'

import { NAV_LINKS, SHELL, bakery } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
}

const LINKS = [{ href: '/', label: 'خانه' }, ...NAV_LINKS]

export const MobileMenu: FC<MobileMenuProps> = ({ open, onClose }) => {
    const router = useRouter()
    const now = useBakeryClock()
    const panelRef = useRef<HTMLDivElement>(null)
    const { mounted, state } = usePresence(open, 300)

    useLockBodyScroll(open)
    useFocusTrap(panelRef, open && mounted, onClose)

    if (!mounted) return null

    const today = now ? getHoursFor(now.weekday) : undefined

    return (
        <div
            ref={panelRef}
            id='mobile-menu'
            role='dialog'
            aria-modal='true'
            aria-label='منوی سایت'
            tabIndex={-1}
            data-state={state}
            className='ld-menu-panel fixed inset-0 z-50 flex flex-col bg-background outline-none lg:hidden'
        >
            <div className={cn(SHELL, 'flex h-16 shrink-0 items-center justify-between border-b border-line')}>
                <Link href='/' onClick={onClose} aria-label='La Douceur، صفحه‌ی اصلی'>
                    <Wordmark />
                </Link>

                <button
                    type='button'
                    onClick={onClose}
                    className='-me-2 inline-flex h-11 cursor-pointer items-center gap-2 px-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-amber'
                >
                    بستن
                    <X className='size-5' aria-hidden='true' />
                </button>
            </div>

            <nav aria-label='منوی موبایل' className={cn(SHELL, 'flex flex-1 flex-col overflow-y-auto overscroll-contain pt-6 pb-[max(2rem,env(safe-area-inset-bottom))]')}>
                <ul>
                    {LINKS.map((link, index) => {
                        const active = link.href === '/' ? router.pathname === '/' : router.pathname.startsWith(link.href)

                        return (
                            <li key={link.href} className='ld-menu-item border-b border-line' style={animationDelay(60 + index * 55)}>
                                <Link
                                    href={link.href}
                                    onClick={onClose}
                                    aria-current={active ? 'page' : undefined}
                                    className='group flex items-baseline justify-between gap-4 py-4 focus-visible:outline-2 focus-visible:outline-amber'
                                >
                                    <span className={cn('font-heading text-[2.6rem] leading-tight sm:text-5xl', active && 'italic')}>
                                        {link.label}
                                    </span>

                                    <span className={cn('label-caps tabular-nums', active ? 'text-amber' : 'text-crust-soft')}>
                                        {toFaDigits(String(index + 1).padStart(2, '0'))}
                                    </span>
                                </Link>
                            </li>
                        )
                    })}
                </ul>

                <div className='ld-menu-item mt-auto space-y-8 pt-10' style={animationDelay(420)}>
                    <ButtonLink href='/order' size='lg' className='w-full' onClick={onClose}>
                        پیش‌سفارش بدهید
                    </ButtonLink>

                    <dl className='grid grid-cols-2 gap-6 text-sm'>
                        <div>
                            <dt className='label-caps text-crust-soft'>امروز</dt>
                            <dd className='mt-2'>{today ? formatRange(today) : 'ساعات کاری را ببینید'}</dd>
                            <dd className='mt-1 text-[0.8125rem]'><OpenStatus /></dd>
                        </div>

                        <div>
                            <dt className='label-caps text-crust-soft'>نشانی ما</dt>
                            <dd className='mt-2'><span dir='ltr'>{bakery.shortAddress}</span></dd>
                            <dd className='mt-1'>
                                <a href={bakery.phoneHref} className='underline decoration-line-strong underline-offset-4' dir='ltr'>
                                    {bakery.phone}
                                </a>
                            </dd>
                        </div>
                    </dl>
                </div>
            </nav>
        </div>
    )
}
